import React, { Suspense } from "react";
import { useSelector } from "react-redux";
import Container from "@mui/material/Container";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import { Routes, Route } from "react-router-dom";
// Core pages - keep these loaded for immediate access
import { Default } from "../pages/default";
import { NotFound } from "../pages/not-found";
import { NotAuth } from "../pages/not-auth";
import { LCM_ReportSMS } from "../pages/lcm/lcm_report-sms";
import { GatewaysCombined } from "../pages/gateways-combined";

// Loading fallback component
const PageLoader = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
    <CircularProgress />
  </Box>
);

// Lazy loaded pages - these will be code split for performance optimization
const CRUD = React.lazy(() => import("../pages/crud").then(module => ({ default: module.CRUD })));
const Report = React.lazy(() => import("../pages/report").then(module => ({ default: module.Report })));

// Campaign Management
const Campaigns = React.lazy(() => import("../pages/campaigns").then(module => ({ default: module.Campaigns })));
const CampaignContacts = React.lazy(() => import("../pages/campaign-contacts").then(module => ({ default: module.CampaignContacts })));
const CallCampaignReport = React.lazy(() => import("../pages/call-campaign-report").then(module => ({ default: module.CallCampaignReport })));

// Call Management
const InboundCalls = React.lazy(() => import("../pages/inbound-calls").then(module => ({ default: module.InboundCalls })));
const OutboundCalls = React.lazy(() => import("../pages/outbound-calls").then(module => ({ default: module.OutboundCalls })));
const TestCall = React.lazy(() => import("../pages/test-call").then(module => ({ default: module.TestCall })));
const Monitor = React.lazy(() => import("../pages/monitor").then(module => ({ default: module.Monitor })));

// Telephony Infrastructure
const Numbers = React.lazy(() => import("../pages/numbers").then(module => ({ default: module.Numbers })));
const Dialplans = React.lazy(() => import("../pages/dialplans").then(module => ({ default: module.Dialplans })));
const SIPAccounts = React.lazy(() => import("../pages/sip-accounts").then(module => ({ default: module.SIPAccounts })));
const InboundRoutes = React.lazy(() => import("../pages/inbound-routes").then(module => ({ default: module.InboundRoutes })));
const OutboundRoutes = React.lazy(() => import("../pages/outbound-routes").then(module => ({ default: module.OutboundRoutes })));
const Gateways = React.lazy(() => import("../pages/gateways").then(module => ({ default: module.Gateways })));

// Reports
const NumberReport = React.lazy(() => import("../pages/number-report").then(module => ({ default: module.NumberReport })));
const OTPReport = React.lazy(() => import("../pages/otp-report").then(module => ({ default: module.OTPReport })));
const ReportKPIAgents = React.lazy(() => import("../pages/report-kpi-agents").then(module => ({ default: module.ReportKPIAgents })));
const ReportCustomers = React.lazy(() => import("../pages/report-customers").then(module => ({ default: module.ReportCustomers })));

// Tools & Utilities
const ContactsTool = React.lazy(() => import("../pages/contact-tool").then(module => ({ default: module.ContactsTool })));
const CRUDList = React.lazy(() => import("../pages/crud__/list").then(module => ({ default: module.CRUDList })));
const CRUD_Page = React.lazy(() => import("../pages/crud/crud-page").then(module => ({ default: module.CRUD_Page })));

// Organization Management
const Posts = React.lazy(() => import("../pages/posts").then(module => ({ default: module.Posts })));
const Partners = React.lazy(() => import("../pages/partners").then(module => ({ default: module.Partners })));
const Staffs = React.lazy(() => import("../pages/staffs").then(module => ({ default: module.Staffs })));
const Departments = React.lazy(() => import("../pages/departments").then(module => ({ default: module.Departments })));
const TeamMembers = React.lazy(() => import("../pages/team-members").then(module => ({ default: module.TeamMembers })));
const ORGUserList = React.lazy(() => import("../pages/user/org-user-list").then(module => ({ default: module.ORGUserList })));

// System Management
const Notifications = React.lazy(() => import("../pages/notifications").then(module => ({ default: module.Notifications })));
const Schedulers = React.lazy(() => import("../pages/schedulers").then(module => ({ default: module.Schedulers })));
const AnnouncementTemplates = React.lazy(() => import("../pages/announcementTemplates").then(module => ({ default: module.AnnouncementTemplates })));
const DataSynchronisation = React.lazy(() => import("../pages/data-sync").then(module => ({ default: module.DataSynchronisation })));

// Dashboards
const DashboardAgent = React.lazy(() => import("../pages/dashboard-agent").then(module => ({ default: module.DashboardAgent })));
const DashboardTeam = React.lazy(() => import("../pages/dashboard-team").then(module => ({ default: module.DashboardTeam })));
const DashboardOrg = React.lazy(() => import("../pages/dashboard-org").then(module => ({ default: module.DashboardOrg })));

// UI & App Management
const Uni_App_Index = React.lazy(() => import("../pages/ui-apps/uni-app-index").then(module => ({ default: module.Uni_App_Index })));
const UI_Uni_Page_Page = React.lazy(() => import("../pages/ui-pages/ui-uni-pages").then(module => ({ default: module.UI_Uni_Page_Page })));

// LCM (Lead Contact Management) Module
const LCMCustomerList = React.lazy(() => import("../pages/lcm-customers/list").then(module => ({ default: module.LCMCustomerList })));
const LCMCustomerDetail = React.lazy(() => import("../pages/lcm-customers/detail").then(module => ({ default: module.LCMCustomerDetail })));
const Contracts = React.lazy(() => import("../pages/contracts").then(module => ({ default: module.Contracts })));
const LCM_Dashboard = React.lazy(() => import("../pages/lcm/lcm_dashboard").then(module => ({ default: module.LCM_Dashboard })));
const LCM_Teams = React.lazy(() => import("../pages/lcm/lcm_teams").then(module => ({ default: module.LCM_Teams })));
const LCM_ImportData = React.lazy(() => import("../pages/lcm/import-data").then(module => ({ default: module.LCM_ImportData })));
const LCM_UpdateCustomers = React.lazy(() => import("../pages/lcm/lcm_update-customers").then(module => ({ default: module.LCM_UpdateCustomers })));
const LCM_Partner_OutboundCalls = React.lazy(() => import("../pages/lcm/lcm_partner_outbound-calls").then(module => ({ default: module.LCM_Partner_OutboundCalls })));
const DataFile_Index = React.lazy(() => import("../pages/lcm_data-files/lcm_data-file_page").then(module => ({ default: module.DataFile_Index })));

// BPO (Business Process Outsourcing) Module
const BPO_Dashboard = React.lazy(() => import("../pages/bpo/dashboard").then(module => ({ default: module.BPO_Dashboard })));
const BPO_Products = React.lazy(() => import("../pages/bpo/product").then(module => ({ default: module.BPO_Products })));

// LXM (Learning Experience Management) Module
const LXM_Dashboard = React.lazy(() => import("../pages/lxm/lxm_dashboard").then(module => ({ default: module.LXM_Dashboard })));
const LXM_CallMonitor = React.lazy(() => import("../pages/lxm/lxm_call-monitor").then(module => ({ default: module.LXM_CallMonitor })));
const LXM_InboundCalls = React.lazy(() => import("../pages/lxm/lxm_inbound-calls").then(module => ({ default: module.LXM_InboundCalls })));
const LXM_OutboundCalls = React.lazy(() => import("../pages/lxm/lxm_outbound-calls").then(module => ({ default: module.LXM_OutboundCalls })));
const LXM_Students = React.lazy(() => import("../pages/lxm/students").then(module => ({ default: module.LXM_Students })));
const LXM_StudentDetails = React.lazy(() => import("../pages/lxm/students/details").then(module => ({ default: module.LXM_StudentDetails })));
const LXM_ManagePermissions = React.lazy(() => import("../pages/lxm/lxm_manage-permissions").then(module => ({ default: module.LXM_ManagePermissions })));
const LXM_History = React.lazy(() => import("../pages/lxm/lxm_history").then(module => ({ default: module.LXM_History })));

// Web Bot Module
const WebBot_Bots_Page = React.lazy(() => import("../pages/web-bot-bot/web-bot-bots").then(module => ({ default: module.WebBot_Bots_Page })));
const WebBot_Account_Page = React.lazy(() => import("../pages/web-bot-account/web-bot-accounts").then(module => ({ default: module.WebBot_Account_Page })));
const WebBot_Proxy_Page = React.lazy(() => import("../pages/web-bot-proxy/web-bot-proxies").then(module => ({ default: module.WebBot_Proxy_Page })));
const WebBot_SMSs_Page = React.lazy(() => import("../pages/web-bot-sms/web-bot-smss").then(module => ({ default: module.WebBot_SMSs_Page })));
const WebBot_Report_Page = React.lazy(() => import("../pages/web-bot-report/web-bot-report").then(module => ({ default: module.WebBot_Report_Page })));

// Third-Party Services
const ThueSim_Dashboard = React.lazy(() => import("../pages/thuesim/dashboard").then(module => ({ default: module.ThueSim_Dashboard })));
const ThueSim_RequestCode = React.lazy(() => import("../pages/thuesim/request-code").then(module => ({ default: module.ThueSim_RequestCode })));

// OFM (Operations & Fleet Management) Module
const OFM_SocketMonitoring = React.lazy(() => import("../pages/ofm/socket-monitoring").then(module => ({ default: module.OFM_SocketMonitoring })));
const OFM_Tools = React.lazy(() => import("../pages/ofm/tools").then(module => ({ default: module.OFM_Tools })));

// Data Management
const Data_CarRegistration = React.lazy(() => import("../pages/data/car-regsitration").then(module => ({ default: module.Data_CarRegistration })));

// LSM (Logistics & Supply Management) Module
const LSM_Dashboard = React.lazy(() => import("../pages/lsm/dashboard").then(module => ({ default: module.LSM_Dashboard })));
const LSM_Shipments = React.lazy(() => import("../pages/lsm-shipments/lxm_shipments").then(module => ({ default: module.LSM_Shipments })));

// XOR Module
const XOR_SimMonitor = React.lazy(() => import("../pages/xor/xor_sim-monitor").then(module => ({ default: module.XOR_SimMonitor })));
const XOR_OTPReport = React.lazy(() => import("../pages/xor/xor_otp-report").then(module => ({ default: module.XOR_OTPReport })));

// XMOR Module
const XMOR_VM = React.lazy(() => import("../pages/xmor/vm/xmor_vm").then(module => ({ default: module.XMOR_VM })));

export function Router(props: any) {
  const navigationItems = props.navigationItems || [];
  const user = useSelector((state: any) => state.user);
  return (
    <Container className={"router-container"} maxWidth={false}>
      <Routes>        
        {navigationItems.map((item: any, i: number) => {
          let page = <NotFound />;
          if (
            item.requireAuthentication === true &&
            user.isLoggedin === false
          ) {
            page = <NotAuth />;
          } else {
            switch (item.pageTemplate) {
              case "inbound-calls": {
                page = <Suspense fallback={<PageLoader />}><InboundCalls /></Suspense>;
                break;
              }
              case "outbound-calls": {
                page = <Suspense fallback={<PageLoader />}><OutboundCalls /></Suspense>;
                break;
              }
              case "campaigns": {
                page = <Suspense fallback={<PageLoader />}><Campaigns /></Suspense>;
                break;
              }
              case "campaign-contacts": {
                page = <Suspense fallback={<PageLoader />}><CampaignContacts /></Suspense>;
                break;
              }
              case "call-campaign-report": {
                page = <Suspense fallback={<PageLoader />}><CallCampaignReport /></Suspense>;
                break;
              }
              case "number-report": {
                page = <Suspense fallback={<PageLoader />}><NumberReport /></Suspense>;
                break;
              }
              case "otp-report": {
                page = <Suspense fallback={<PageLoader />}><OTPReport /></Suspense>;
                break;
              }
              case "crud": {
                page = <Suspense fallback={<PageLoader />}><CRUD /></Suspense>;
                break;
              }
              case "crud-list": {
                page = <Suspense fallback={<PageLoader />}><CRUDList pageId={item.pageId} /></Suspense>;
                break;
              }
              case "report": {
                page = <Suspense fallback={<PageLoader />}><Report /></Suspense>;
                break;
              }
              case "dashboard": {
                page = <Default />;
                break;
              }
              case "test-call": {
                page = <Suspense fallback={<PageLoader />}><TestCall /></Suspense>;
                break;
              }
              case "numbers": {
                page = <Suspense fallback={<PageLoader />}><Numbers /></Suspense>;
                break;
              }
              case "dialplans": {
                page = <Suspense fallback={<PageLoader />}><Dialplans /></Suspense>;
                break;
              }
              case "sip-accounts": {
                page = <Suspense fallback={<PageLoader />}><SIPAccounts /></Suspense>;
                break;
              }
              case "inbound-routes": {
                page = <Suspense fallback={<PageLoader />}><InboundRoutes /></Suspense>;
                break;
              }
              case "outbound-routes": {
                page = <Suspense fallback={<PageLoader />}><OutboundRoutes /></Suspense>;
                break;
              }
              case "monitor": {
                page = <Suspense fallback={<PageLoader />}><Monitor /></Suspense>;
                break;
              }
              case "contact-tool": {
                page = <Suspense fallback={<PageLoader />}><ContactsTool /></Suspense>;
                break;
              }
              case "lcm_posts": {
                page = <Suspense fallback={<PageLoader />}><Posts /></Suspense>;
                break;
              }
              case "lcm_partners": {
                page = <Suspense fallback={<PageLoader />}><Partners /></Suspense>;
                break;
              }
              case "lcm_staffs": {
                page = <Suspense fallback={<PageLoader />}><Staffs /></Suspense>;
                break;
              }
              case "lcm_teams": {
                page = <Suspense fallback={<PageLoader />}><LCM_Teams /></Suspense>;
                break;
              }
              case "lcm_contracts": {
                page = <Suspense fallback={<PageLoader />}><Contracts /></Suspense>;
                break;
              }
              case "lcm-customer-list": {
                page = <Suspense fallback={<PageLoader />}><LCMCustomerList /></Suspense>;
                break;
              }
              case "lcm-customer-detail": {
                page = <Suspense fallback={<PageLoader />}><LCMCustomerDetail /></Suspense>;
                break;
              }
              case "lcm_schedulers": {
                page = <Suspense fallback={<PageLoader />}><Schedulers /></Suspense>;
                break;
              }
              case "lcm_update-customers": {
                page = <Suspense fallback={<PageLoader />}><LCM_UpdateCustomers /></Suspense>;
                break;
              }
              case "lcm_gsm-gateway": {
                page = <Suspense fallback={<PageLoader />}><GatewaysCombined /></Suspense>;
                break;
              }
              case "notifications": {
                page = <Suspense fallback={<PageLoader />}><Notifications /></Suspense>;
                break;
              }
              case "report_lcm_agents": {
                page = <Suspense fallback={<PageLoader />}><ReportKPIAgents /></Suspense>;
                break;
              }
              case "report_lcm_customers": {
                page = <Suspense fallback={<PageLoader />}><ReportCustomers /></Suspense>;
                break;
              }
              case "dashboard_agent": {
                page = <Suspense fallback={<PageLoader />}><DashboardAgent /></Suspense>;
                break;
              }
              case "dashboard_team": {
                page = <Suspense fallback={<PageLoader />}><DashboardTeam /></Suspense>;
                break;
              }
              case "dashboard_org": {
                page = <Suspense fallback={<PageLoader />}><DashboardOrg /></Suspense>;
                break;
              }
              case "lcm_announcement_templates": {
                page = <Suspense fallback={<PageLoader />}><AnnouncementTemplates /></Suspense>;
                break;
              }
              case "lcm_data_synchronisation": {
                page = <Suspense fallback={<PageLoader />}><DataSynchronisation /></Suspense>;
                break;
              }
              case "lcm_dashboard": {
                page = <Suspense fallback={<PageLoader />}><LCM_Dashboard /></Suspense>;
                break;
              }
              case "lcm_team": {
                page = <Suspense fallback={<PageLoader />}><Departments /></Suspense>;
                break;
              }
              case "lcm_staff_assignment": {
                page = <Suspense fallback={<PageLoader />}><TeamMembers /></Suspense>;
                break;
              }
              case "lcm_report-sms": {
                page = <Suspense fallback={<PageLoader />}><LCM_ReportSMS /></Suspense>;
                break;
              }
              case "org-user-list": {
                page = <Suspense fallback={<PageLoader />}><ORGUserList /></Suspense>;
                break;
              }
              case "uni-app-index": {
                page = <Suspense fallback={<PageLoader />}><Uni_App_Index/></Suspense>;
                break;
              }
              case "ui_uni_page": {
                page = <Suspense fallback={<PageLoader />}><UI_Uni_Page_Page pageId={item.pageId}/></Suspense>;
                break;
              }
              case "data-file": {
                page = <Suspense fallback={<PageLoader />}><DataFile_Index/></Suspense>;
                break;
              }
              case "lcm_data-import": {
                page = <Suspense fallback={<PageLoader />}><LCM_ImportData/></Suspense>;
                break;
              }
              case "web-bot_bot": {
                page = <Suspense fallback={<PageLoader />}><WebBot_Bots_Page/></Suspense>;
                break;
              }
              case "web-bot_account": {
                page = <Suspense fallback={<PageLoader />}><WebBot_Account_Page/></Suspense>;
                break;
              }
              case "web-bot_proxy": {
                page = <Suspense fallback={<PageLoader />}><WebBot_Proxy_Page/></Suspense>;
                break;
              }
              case "web-bot_sms": {
                page = <Suspense fallback={<PageLoader />}><WebBot_SMSs_Page/></Suspense>;
                break;
              }
              case "web-bot_report": {
                page = <Suspense fallback={<PageLoader />}><WebBot_Report_Page/></Suspense>;
                break;
              }
              case "crud_page": {
                page = <Suspense fallback={<PageLoader />}><CRUD_Page pageId={item.pageId}/></Suspense>
                break;
              }
              case "thuesim_dashboard": {
                page = <Suspense fallback={<PageLoader />}><ThueSim_Dashboard/></Suspense>
                break;
              }
              case "thuesim_request-code": {
                page = <Suspense fallback={<PageLoader />}><ThueSim_RequestCode/></Suspense>
                break;
              }
              case "ofm_socket-monitoring": {
                page = <Suspense fallback={<PageLoader />}><OFM_SocketMonitoring/></Suspense>
                break;
              }
              case "bpo_dashboard": {
                page = <Suspense fallback={<PageLoader />}><BPO_Dashboard/></Suspense>
                break;
              }
              case "bpo_products": {
                page = <Suspense fallback={<PageLoader />}><BPO_Products/></Suspense>
                break;
              }
              case "data_car-registration": {
                page = <Suspense fallback={<PageLoader />}><Data_CarRegistration/></Suspense>
                break;
              }
              case "lxm_dashboard": {
                page = <Suspense fallback={<PageLoader />}><LXM_Dashboard/></Suspense>
                break;
              }
              case "lxm_call-monitor": {
                page = <Suspense fallback={<PageLoader />}><LXM_CallMonitor/></Suspense>
                break;
              }
              case "lxm_inbound-calls": {
                page = <Suspense fallback={<PageLoader />}><LXM_InboundCalls/></Suspense>
                break;
              }
              case "lxm_outbound-calls": {
                page = <Suspense fallback={<PageLoader />}><LXM_OutboundCalls/></Suspense>
                break;
              }
              case "lxm_students": {
                page = <Suspense fallback={<PageLoader />}><LXM_Students/></Suspense>
                break;
              }
              case "lxm_manage-permissions": {
                page = <Suspense fallback={<PageLoader />}><LXM_ManagePermissions /></Suspense>;
                break;
              }
              case "lxm_history": {
                page = <Suspense fallback={<PageLoader />}><LXM_History /></Suspense>;
                break;
              }
              case "lsm_dashboard": {
                page = <Suspense fallback={<PageLoader />}><LSM_Dashboard/></Suspense>
                break;
              }
              case "xor_sim-monitor": {
                page = <Suspense fallback={<PageLoader />}><XOR_SimMonitor/></Suspense>
                break;
              }
              case "xor_otp-report": {
                page = <Suspense fallback={<PageLoader />}><XOR_OTPReport/></Suspense>
                break;
              }
              case "ofm_tools": {
                page = <Suspense fallback={<PageLoader />}><OFM_Tools/></Suspense>
                break;
              }
              case "xmor_vms": {
                page = <Suspense fallback={<PageLoader />}><XMOR_VM/></Suspense>
                break;
              }
              case "lsm_shipments": {
                page = <Suspense fallback={<PageLoader />}><LSM_Shipments/></Suspense>
                break;
              }
              case "lcm_partner_outbound-calls": {
                page = <Suspense fallback={<PageLoader />}><LCM_Partner_OutboundCalls/></Suspense>
                break;
              }
              default: {
                page = <NotFound />;
                break;
              }
            }
          }
          return <Route key={i} path={item.path} element={page} />;
        })}
        <Route path="/" element={<Default />} />
        <Route path="/bpo/dashboard" element={<Suspense fallback={<PageLoader />}><BPO_Dashboard /></Suspense>}/>
        <Route path="/bpo/products" element={<Suspense fallback={<PageLoader />}><BPO_Products /></Suspense>}/>
        <Route path="/thuesim/dashboard" element={<Suspense fallback={<PageLoader />}><ThueSim_Dashboard /></Suspense>}/>
        <Route path="/thuesim/request-code" element={<Suspense fallback={<PageLoader />}><ThueSim_RequestCode /></Suspense>}/>
        <Route path="/ofm/socket-monitoring" element={<Suspense fallback={<PageLoader />}><OFM_SocketMonitoring /></Suspense>}/>
        <Route path="/ofm/tools" element={<Suspense fallback={<PageLoader />}><OFM_Tools /></Suspense>}/>
        <Route path="/lcm/customer/:id" element={<Suspense fallback={<PageLoader />}><LCMCustomerDetail /></Suspense>}/>
        <Route path="/data/car-registration" element={<Suspense fallback={<PageLoader />}><Data_CarRegistration /></Suspense>}/>
        <Route path="/lxm/call-monitor" element={<Suspense fallback={<PageLoader />}><LXM_CallMonitor /></Suspense>}/>
        <Route path="/lxm/inbound-calls" element={<Suspense fallback={<PageLoader />}><LXM_InboundCalls /></Suspense>}/>
        <Route path="/lxm/outbound-calls" element={<Suspense fallback={<PageLoader />}><LXM_OutboundCalls /></Suspense>}/>
        <Route path="/lxm/students" element={<Suspense fallback={<PageLoader />}><LXM_Students /></Suspense>}/>
        <Route path="/lxm/history" element={<Suspense fallback={<PageLoader />}><LXM_History /></Suspense>}/>
        <Route path="/lxm/students/:id" element={<Suspense fallback={<PageLoader />}><LXM_StudentDetails /></Suspense>}/>
        <Route path="/lxm/manage-permissions" element={<Suspense fallback={<PageLoader />}><LXM_ManagePermissions /></Suspense>}/>
        <Route path="/xmor/vms" element={<Suspense fallback={<PageLoader />}><XMOR_VM /></Suspense>}/>
        <Route path="/lsm/shipments" element={<Suspense fallback={<PageLoader />}><LSM_Shipments /></Suspense>}/>
        <Route path="/lcm/partner_outbound-calls" element={<Suspense fallback={<PageLoader />}><LCM_Partner_OutboundCalls /></Suspense>}/>
        <Route path="/lcm/update_customers" element={<Suspense fallback={<PageLoader />}><LCM_UpdateCustomers /></Suspense>}/>
        <Route path="/lcm/dashboard" element={<Suspense fallback={<PageLoader />}><LCM_Dashboard /></Suspense>}/>
        
        
        <Route path="/*" element={<NotFound />} />
      </Routes>
    </Container>
  );
}
